"use client";
import React, { createContext, useContext, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import useIsAtBottom from "domains/commons/hooks/useIsAtBottom";
import useRouter from "domains/navigation/hooks/useRouter";
import { FOOTER_HEIGHT } from "domains/ui/components/Footer";

import { InkeepChatButtonProps, InkeepCustomTriggerProps } from "@inkeep/uikit";
import {
  InkeepBaseSettings,
  InkeepModalSettings,
} from "@inkeep/uikit/dist/components/InkeepWidgetProps";

const baseSettings: InkeepBaseSettings = {
  apiKey: process.env.NEXT_PUBLIC_INKEEP_API_KEY!,
  integrationId: process.env.NEXT_PUBLIC_INKEEP_INTEGRATION_ID!,
  organizationId: process.env.NEXT_PUBLIC_INKEEP_ORGANIZATION_ID!,
  primaryBrandColor: "#0088CC",
  organizationDisplayName: "Scenario",
  env: process.env.NODE_ENV !== "production" ? "DEVELOPMENT" : "PRODUCTION",
  colorMode: {
    forcedColorMode: "dark", // options: 'light' or dark'
  },
};

const aiChatSettings = {
  botAvatarSrcUrl: "/icon.png",
  quickQuestions: [
    "How to turn an image into a video?",
    "Do you have an API?",
    "Shall I use Flux Kontext or GPT-Image?",
    "Is my data kept private?",
    "What's a custom-trained model?",
    "How to write effective prompts?",
    "Is multi-user support available?",
  ],
};

const modalSettings: InkeepModalSettings = {
  defaultView: "AI_CHAT",
};

const InkeepCustomTrigger = dynamic(
  () => import("@inkeep/uikit").then((mod) => mod.InkeepCustomTrigger),
  {
    ssr: false,
  }
);
const InkeepChatButton = dynamic(
  () => import("@inkeep/uikit").then((mod) => mod.InkeepChatButton),
  {
    ssr: false,
  }
);

interface ChatProviderContext {
  setIsChatVisible: (isChatVisible: boolean) => void;
  isChatVisible: boolean;
}

export const ChatContext = createContext<ChatProviderContext>({
  setIsChatVisible: () => {},
  isChatVisible: false,
});

export function ChatProvider({
  children = <></>,
}: {
  children?: React.ReactNode;
}) {
  const router = useRouter();
  const [isChatVisible, setIsChatVisible] = useState<boolean>(false);

  const isFooterAtTheBottom = useMemo(() => {
    return router.pathname === "/";
  }, [router.pathname]);
  const isFooterAlwaysVisible = useMemo(() => {
    return (
      router.pathname.startsWith("/team") ||
      router.pathname === "/profile" ||
      router.pathname === "/login"
    );
  }, [router.pathname]);

  const inkeepCustomTriggerProps: InkeepCustomTriggerProps = useMemo(
    () => ({
      isOpen: isChatVisible,
      onClose: () => setIsChatVisible(false),
      baseSettings,
      aiChatSettings,
      modalSettings,
    }),
    [isChatVisible]
  );

  // Detect if we are at the very bottom of the page
  const isAtBottom = useIsAtBottom(
    isFooterAtTheBottom
      ? {
          offset: 40,
        }
      : {
          skip: true,
        }
  );
  const inkeepChatButtonProps: InkeepChatButtonProps = useMemo(
    () => ({
      chatButtonType: "PILL",
      baseSettings,
      aiChatSettings,
      modalSettings,
      fixedPositionYOffset:
        isAtBottom || isFooterAlwaysVisible
          ? `${FOOTER_HEIGHT + 10}px`
          : "1.5rem",
    }),
    [isAtBottom, isFooterAlwaysVisible]
  );

  return (
    <ChatContext.Provider
      value={{
        setIsChatVisible,
        isChatVisible,
      }}
    >
      {children}
      <InkeepCustomTrigger {...inkeepCustomTriggerProps} />
      <InkeepChatButton {...inkeepChatButtonProps} />
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  return useContext<ChatProviderContext>(ChatContext);
}
